// Institute of Design and Innovation (email idi@unm.edu). //

import { AxiosResponse } from 'axios'
import httpClient from './httpClient'
import User from '../models/user'
import UserAvailability from '@/models/userAvailability'
import Permission from '@/models/permission'
import BlockedTime from '@/models/blockedTime'
import OutlookIntegration from '@/models/outlookIntegration'

/**
 * Service containing all User API requests
 */
export default class UserService {
  /**
   * Creates a new blocked time
   * @param userId The id of the user
   * @param blockedTime The blocked time to create
   */
  static createBlockedTime (userId: number, blockedTime: BlockedTime): Promise<AxiosResponse<BlockedTime>> {
    const body = {
      startTime: blockedTime.startTime.toISOString(),
      endTime: blockedTime.endTime.toISOString(),
      title: blockedTime.title
    }
    return httpClient.post(`userBlockedTime/createForUser/${userId}`, body)
  }

  /**
   * Marks the given user an OSP Staff member
   * @param netid The netid of the User
   */
  static createOspStaff (netid: string): Promise<AxiosResponse<User>> {
    return httpClient.post(`/user/createUser/${netid}`)
  }

  /**
   * Returns the availability of the given User
   * @param id The Id of the User
   */
  static getAvailability (id: number): Promise<AxiosResponse<UserAvailability>> {
    return httpClient.get(`/user/${id}/availability`)
  }

  /**
   * Returns all OSP Staff memebers
   */
  static getOspStaff (): Promise<AxiosResponse<Array<User>>> {
    return httpClient.get('/user/ospstaff')
  }

  /**
   * Returns the given User
   * @param id The Id of the User
   */
  static getUser (id: number): Promise<AxiosResponse<User>> {
    return httpClient.get(`/user/${id}`)
  }

  /**
   * Searches users using their username
   * @param username The username to search for
   */
  static searchUser (username: string): Promise<AxiosResponse<User>> {
    return httpClient.get(`/user/${username}/getUserFromOds`)
  }

  /**
   * Updates a user's availability
   * @param id The Id of the User
   * @param availability The updated Availability
   */
  static updateAvailability (id: number, availability: Array<UserAvailability>): Promise<AxiosResponse<User>> {
    return httpClient.put(`/user/${id}/availability`, availability)
  }

  /**
   * Updates the given User's permissions
   * @param userId The Id of the User
   * @param permissions the set of permissions
   */
  static updateUserPermissions (userId: number, permissions: Array<Permission>): Promise<AxiosResponse<User>> {
    return httpClient.put(`/user/${userId}/setPermissions`, permissions)
  }

  /**
   * Removes OSP Staff status from the given user
   * @param userId The Id of the User
   */
  static removeOspStaff (userId: number): Promise<AxiosResponse<User>> {
    return httpClient.put(`/user/${userId}/removeOspStaff`)
  }

  /**
   * Enable Outlook integration for given user
   * @param id The Id of the User
   * @param authCode The authentication code for the User
   */
  static enableOutlookIntegration (args: OutlookIntegration): Promise<AxiosResponse<User>> {
    return httpClient.put(`/user/${args.id}/enableOutlook`, args)
  }

  /**
   * Disable Outlook integration for given user
   * @param id The Id of the User
   * @param authCode The authentication code for the User
   */
  static disableOutlookIntegration (args: OutlookIntegration): Promise<AxiosResponse<User>> {
    return httpClient.put(`/user/${args.id}/disableOutlook`, args)
  }

  /**
   * Check Outlook Syncing in progress
   * @param userId The Id of the User
   * @returns value of outlook syncing field in user table
   */
  static isOutlookSyncing (userId: number): Promise<AxiosResponse<User>> {
    return httpClient.get(`/user/${userId}/outlookSyncing`)
  }
}
